import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersAPI, qrCodeAPI, attendanceAPI, manpowerAPI, exportAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { QRCodeSVG } from 'qrcode.react';
import { Users, UserCheck, UserX, Phone, Mail, ChevronRight, ChevronLeft, QrCode, MapPin, IndianRupee, Download, Copy, Check, Sun, Moon, Clock, Calendar, FileSpreadsheet, HardHat, Trash2, ArrowUpDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const Team = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamAttendanceStats, setTeamAttendanceStats] = useState({ present: 0, absent: 0, notMarked: 0 });
  const [loading, setLoading] = useState(true);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrGenerated, setQrGenerated] = useState(null);
  const [qrCopied, setQrCopied] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);
  
  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportType, setExportType] = useState('month'); // 'month' or 'dateRange'
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [includeManpower, setIncludeManpower] = useState(true);
  
  // Manpower state with month/year selector
  const [manpowerEntries, setManpowerEntries] = useState([]);
  const [manpowerLoading, setManpowerLoading] = useState(false);
  const [selectedManpower, setSelectedManpower] = useState(null);
  const [showManpowerDetail, setShowManpowerDetail] = useState(false);
  const [manpowerMonth, setManpowerMonth] = useState(new Date().getMonth() + 1);
  const [manpowerYear, setManpowerYear] = useState(new Date().getFullYear());
  const [manpowerSortOrder, setManpowerSortOrder] = useState('desc'); // 'asc' or 'desc'
  
  const today = new Date().toISOString().split('T')[0];
  const [qrForm, setQrForm] = useState({
    location: '',
    conveyance_amount: '',
    date: today,
    shift_type: 'day',
    shift_start: '09:45',
    shift_end: '19:00'
  });

  // Preset shifts
  const shiftPresets = {
    day: { start: '09:45', end: '19:00', label: 'Day Shift (9:45 AM - 7:00 PM)' },
    night: { start: '21:00', end: '06:00', label: 'Night Shift (9:00 PM - 6:00 AM)' }
  };

  useEffect(() => {
    loadTeamMembers();
  }, [user?.id]);

  const handleShiftChange = (shiftType) => {
    const preset = shiftPresets[shiftType];
    setQrForm({
      ...qrForm,
      shift_type: shiftType,
      shift_start: preset.start,
      shift_end: preset.end
    });
  };

  useEffect(() => {
    loadTeamMembers();
  }, [user?.id]);

  const loadTeamMembers = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const members = await usersAPI.getTeamMembers(user.id);
      setTeamMembers(members || []);
      
      // Calculate actual attendance stats for today
      if (members && members.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const attendancePromises = members.map(m => 
          attendanceAPI.getAll(m.id, today).catch(() => [])
        );
        const attendanceResults = await Promise.all(attendancePromises);
        
        let presentCount = 0;
        let absentCount = 0;
        let notMarkedCount = 0;
        
        attendanceResults.forEach((attendance) => {
          if (attendance && attendance.length > 0) {
            const record = attendance[0];
            if (record.status === 'present' || record.attendance_status === 'full_day' || record.attendance_status === 'half_day') {
              presentCount++;
            } else if (record.status === 'absent' || record.attendance_status === 'absent') {
              absentCount++;
            } else {
              notMarkedCount++;
            }
          } else {
            notMarkedCount++;
          }
        });
        
        setTeamAttendanceStats({ present: presentCount, absent: absentCount, notMarked: notMarkedCount });
      }
      
      // Load manpower entries for this team lead
      await loadManpowerEntries();
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load manpower entries assigned to this team lead for selected month
  const loadManpowerEntries = async (year = manpowerYear, month = manpowerMonth) => {
    if (!user?.id) return;
    setManpowerLoading(true);
    try {
      const manpower = await manpowerAPI.getByMonth(year, month, user.id);
      setManpowerEntries(manpower || []);
    } catch (error) {
      console.error('Error loading manpower:', error);
      setManpowerEntries([]);
    } finally {
      setManpowerLoading(false);
    }
  };

  // Change manpower month
  const changeManpowerMonth = (direction) => {
    let newMonth = manpowerMonth + direction;
    let newYear = manpowerYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    
    setManpowerMonth(newMonth);
    setManpowerYear(newYear);
    loadManpowerEntries(newYear, newMonth);
  };

  // Get month name
  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1];
  };

  // Handle export
  const handleExport = () => {
    let exportUrl;
    if (exportType === 'month') {
      exportUrl = exportAPI.teamAttendance(user.id, null, null, exportMonth, exportYear, includeManpower);
    } else {
      if (!exportStartDate || !exportEndDate) {
        toast.error('Please select start and end dates');
        return;
      }
      exportUrl = exportAPI.teamAttendance(user.id, exportStartDate, exportEndDate, null, null, includeManpower);
    }
    
    window.open(exportUrl, '_blank');
    toast.success('Export started');
    setShowExportDialog(false);
  };

  // Group manpower entries by person name
  const groupedManpower = manpowerEntries.reduce((acc, entry) => {
    const name = entry.name?.trim().toLowerCase() || 'unknown';
    if (!acc[name]) {
      acc[name] = {
        name: entry.name,
        entries: [],
        totalEarned: 0,
        totalConveyance: 0,
        daysWorked: 0
      };
    }
    acc[name].entries.push(entry);
    acc[name].totalEarned += (entry.daily_earned || 0);
    acc[name].totalConveyance += (entry.conveyance || 0);
    acc[name].daysWorked += 1;
    return acc;
  }, {});

  const uniqueManpowerPersons = Object.values(groupedManpower);

  // Sort manpower entries by date
  const sortedManpowerEntries = [...manpowerEntries].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return manpowerSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Toggle sort order
  const toggleManpowerSort = () => {
    setManpowerSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  // Handle clicking on a manpower person to view their monthly record
  const handleManpowerClick = (person) => {
    setSelectedManpower(person);
    setShowManpowerDetail(true);
  };

  const handleGenerateQR = async () => {
    if (!qrForm.location || !qrForm.conveyance_amount) {
      toast.error('Please fill location and conveyance amount');
      return;
    }
    
    setGeneratingQR(true);
    try {
      const result = await qrCodeAPI.create({
        location: qrForm.location,
        conveyance_amount: parseFloat(qrForm.conveyance_amount),
        date: qrForm.date,
        created_by: user.id,
        shift_type: qrForm.shift_type,
        shift_start: qrForm.shift_start,
        shift_end: qrForm.shift_end
      });
      
      setQrGenerated(result);
      toast.success('QR Code generated successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to generate QR code');
    } finally {
      setGeneratingQR(false);
    }
  };

  const copyQRData = () => {
    if (qrGenerated?.qr_data) {
      navigator.clipboard.writeText(qrGenerated.qr_data);
      setQrCopied(true);
      toast.success('QR data copied to clipboard!');
      setTimeout(() => setQrCopied(false), 2000);
    }
  };

  const downloadQR = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Generate high resolution image (3x scale for better quality)
      const scale = 3;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      
      const link = document.createElement('a');
      link.download = `QR_${qrForm.location}_${qrForm.date}.png`;
      link.href = canvas.toDataURL('image/png', 1.0); // Maximum quality
      link.click();
      toast.success('High resolution QR downloaded!');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const resetQRDialog = () => {
    setShowQRDialog(false);
    setQrGenerated(null);
    setQrForm({
      location: '',
      conveyance_amount: '',
      date: today,
      shift_type: 'day',
      shift_start: '09:45',
      shift_end: '19:00'
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-800">My Team</h1>
          <p className="text-sm text-gray-500">{user.department} Department</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowExportDialog(true)}
            variant="outline"
            className="border-green-600 text-green-600 hover:bg-green-50"
            data-testid="export-team-btn"
          >
            <FileSpreadsheet size={18} className="mr-2" /> Export
          </Button>
          <Button 
            onClick={() => setShowQRDialog(true)}
            className="bg-[#1E2A5E] hover:bg-[#2D3A8C]"
            data-testid="generate-qr-btn"
          >
            <QrCode size={18} className="mr-2" /> Generate QR
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-2">
        <Card>
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 mx-auto mb-2 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users size={20} className="text-blue-600" />
            </div>
            <p className="text-xl font-bold text-gray-800">{teamMembers.length}</p>
            <p className="text-[10px] text-gray-500">Employees</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 mx-auto mb-2 bg-purple-100 rounded-xl flex items-center justify-center">
              <HardHat size={20} className="text-purple-600" />
            </div>
            <p className="text-xl font-bold text-purple-600">{uniqueManpowerPersons.length}</p>
            <p className="text-[10px] text-gray-500">Manpower</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 mx-auto mb-2 bg-green-100 rounded-xl flex items-center justify-center">
              <UserCheck size={20} className="text-green-600" />
            </div>
            <p className="text-xl font-bold text-green-600">{teamAttendanceStats.present}</p>
            <p className="text-[10px] text-gray-500">Present</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 mx-auto mb-2 bg-red-100 rounded-xl flex items-center justify-center">
              <UserX size={20} className="text-red-600" />
            </div>
            <p className="text-xl font-bold text-red-600">{teamAttendanceStats.absent}</p>
            <p className="text-[10px] text-gray-500">Absent</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 mx-auto mb-2 bg-gray-100 rounded-xl flex items-center justify-center">
              <Clock size={20} className="text-gray-600" />
            </div>
            <p className="text-xl font-bold text-gray-600">{teamAttendanceStats.notMarked}</p>
            <p className="text-[10px] text-gray-500">Not Marked</p>
          </CardContent>
        </Card>
      </div>

      {/* Team Members List */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Team Members</h3>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No team members assigned</div>
          ) : (
            <div className="space-y-3">
              {teamMembers.map((member) => (
                <div key={member.id} className="p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {member.name?.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 text-sm sm:text-base truncate">{member.name}</p>
                        <p className="text-xs text-gray-500 truncate">{member.designation}</p>
                      </div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium flex-shrink-0">
                      Active
                    </span>
                  </div>
                  {/* Contact Info - Stack on mobile */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500">
                      <a href={`tel:${member.phone}`} className="flex items-center gap-1 hover:text-[#1E2A5E]">
                        <Phone size={12} /> <span className="hidden sm:inline">{member.phone}</span><span className="sm:hidden">Call</span>
                      </a>
                      <a href={`mailto:${member.email}`} className="flex items-center gap-1 hover:text-[#1E2A5E]">
                        <Mail size={12} /> Email
                      </a>
                    </div>
                    {/* Action Buttons - Horizontal Scroll on Mobile */}
                    <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => navigate(`/attendance/employee-attendance/${member.id}`)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50 flex-shrink-0 text-xs"
                      >
                        <Calendar size={14} className="mr-1" /> View
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => navigate(`/attendance/payslip/${member.id}`)}
                        className="text-green-600 border-green-200 hover:bg-green-50 flex-shrink-0 text-xs"
                      >
                        <IndianRupee size={14} className="mr-1" /> Payslip
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manpower Entries Section - Grouped by Person with Month Navigation */}
      <Card data-testid="manpower-section">
        <CardContent className="p-4">
          {/* Header with Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <HardHat size={18} className="text-purple-600" />
              Manpower Sourcing
            </h3>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeManpowerMonth(-1)}
                className="h-8 w-8 p-0"
                data-testid="prev-month-btn"
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm font-medium min-w-[120px] text-center">
                {getMonthName(manpowerMonth)} {manpowerYear}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeManpowerMonth(1)}
                className="h-8 w-8 p-0"
                disabled={manpowerMonth === new Date().getMonth() + 1 && manpowerYear === new Date().getFullYear()}
                data-testid="next-month-btn"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
          
          {manpowerLoading ? (
            <div className="text-center py-6 text-gray-500">Loading manpower...</div>
          ) : uniqueManpowerPersons.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <HardHat size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm">No manpower entries for {getMonthName(manpowerMonth)} {manpowerYear}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Sort Toggle */}
              <div className="flex items-center justify-between text-xs text-gray-500 pb-2 border-b">
                <span>{sortedManpowerEntries.length} entries</span>
                <button 
                  onClick={toggleManpowerSort}
                  className="flex items-center gap-1 hover:text-purple-600 transition-colors"
                >
                  <ArrowUpDown size={14} />
                  Date: {manpowerSortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                </button>
              </div>
              
              {/* Individual Entries with Date and Location */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {sortedManpowerEntries.map((entry, idx) => (
                  <div 
                    key={entry.id || idx} 
                    className="p-3 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors"
                    data-testid={`manpower-entry-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-sm">
                          {entry.name?.split(' ').map(n => n[0]).join('').slice(0,2) || 'MP'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{entry.name}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                            <span className="flex items-center gap-1 bg-white px-2 py-0.5 rounded">
                              <Calendar size={10} className="text-purple-500" />
                              {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                            <span className="flex items-center gap-1 bg-white px-2 py-0.5 rounded">
                              <MapPin size={10} className="text-blue-500" />
                              {entry.location || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-purple-700">₹{entry.daily_earned || 0}</p>
                        {entry.conveyance > 0 && (
                          <p className="text-xs text-green-600">+₹{entry.conveyance} conv</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Summary */}
              <div className="mt-3 pt-3 border-t flex justify-between text-sm">
                <span className="text-gray-600">{uniqueManpowerPersons.length} worker{uniqueManpowerPersons.length > 1 ? 's' : ''} • {manpowerEntries.length} entries</span>
                <span className="font-semibold text-purple-700">
                  ₹{manpowerEntries.reduce((sum, m) => sum + (m.daily_earned || 0) + (m.conveyance || 0), 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Quick Actions</h3>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/attendance/team-attendance')}
              className="w-full flex items-center justify-between p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
            >
              <span className="font-medium text-blue-700">View Team Attendance</span>
              <ChevronRight size={18} className="text-blue-500" />
            </button>
            <button
              onClick={() => navigate('/attendance/leaves')}
              className="w-full flex items-center justify-between p-3 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors"
            >
              <span className="font-medium text-orange-700">Manage Leave Requests</span>
              <ChevronRight size={18} className="text-orange-500" />
            </button>
            <button
              onClick={() => navigate('/attendance/bills')}
              className="w-full flex items-center justify-between p-3 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors"
            >
              <span className="font-medium text-purple-700">Review Bill Submissions</span>
              <ChevronRight size={18} className="text-purple-500" />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* QR Code Generation Dialog */}
      <Dialog open={showQRDialog} onOpenChange={resetQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode size={20} />
              Generate Attendance QR Code
            </DialogTitle>
          </DialogHeader>
          
          {!qrGenerated ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={qrForm.date}
                  onChange={(e) => setQrForm({...qrForm, date: e.target.value})}
                />
              </div>
              
              {/* Shift Selection */}
              <div className="space-y-2">
                <Label>Shift Type</Label>
                <Select value={qrForm.shift_type} onValueChange={handleShiftChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select shift" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">
                      <div className="flex items-center gap-2">
                        <Sun size={16} className="text-yellow-500" />
                        Day Shift (9:45 AM - 7:00 PM)
                      </div>
                    </SelectItem>
                    <SelectItem value="night">
                      <div className="flex items-center gap-2">
                        <Moon size={16} className="text-blue-500" />
                        Night Shift (9:00 PM - 6:00 AM)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Custom Shift Timing */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Clock size={14} /> Shift Start
                  </Label>
                  <Input
                    type="time"
                    value={qrForm.shift_start}
                    onChange={(e) => setQrForm({...qrForm, shift_start: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Clock size={14} /> Shift End
                  </Label>
                  <Input
                    type="time"
                    value={qrForm.shift_end}
                    onChange={(e) => setQrForm({...qrForm, shift_end: e.target.value})}
                  />
                </div>
              </div>
              
              {/* Attendance Rules Info */}
              <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-800">
                <p className="font-semibold mb-1">Attendance Rules (Shift: 9:45 AM - 7:00 PM):</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Scan 9:45 - 10:15 AM → <span className="text-green-600 font-medium">Full Day</span> (recorded as 9:45)</li>
                  <li>Scan 10:16 AM - 12:45 PM → <span className="text-yellow-600 font-medium">Half Day</span></li>
                  <li>Scan after 12:45 PM → <span className="text-red-600 font-medium">Absent</span></li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  placeholder="e.g., Client Site A, Office Building B"
                  value={qrForm.location}
                  onChange={(e) => setQrForm({...qrForm, location: e.target.value})}
                />
                <p className="text-xs text-gray-500">
                  Enter the work location for today&apos;s attendance
                </p>
              </div>
              <div className="space-y-2">
                <Label>Conveyance Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={qrForm.conveyance_amount}
                  onChange={(e) => setQrForm({...qrForm, conveyance_amount: e.target.value})}
                />
                <p className="text-xs text-gray-500">
                  Full amount for Full Day, half for Half Day
                </p>
              </div>
              
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleGenerateQR} 
                  className="bg-[#1E2A5E] hover:bg-[#2D3A8C]"
                  disabled={generatingQR}
                >
                  {generatingQR ? 'Generating...' : 'Generate QR'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* QR Code Display */}
              <div className="flex flex-col items-center">
                <div className="p-6 bg-white border-2 border-gray-200 rounded-xl shadow-sm">
                  <QRCodeSVG 
                    id="qr-code-svg"
                    value={qrGenerated.qr_data}
                    size={280}
                    level="H"
                    includeMargin={true}
                    imageSettings={{
                      excavate: true,
                    }}
                  />
                </div>
                <p className="text-xs text-green-600 mt-2 font-medium">
                  ✓ High resolution QR for easy scanning
                </p>
              </div>
              
              {/* QR Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Date</span>
                  <span className="font-medium">{qrGenerated.date}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    {qrGenerated.shift_type === 'day' ? <Sun size={14} className="text-yellow-500" /> : <Moon size={14} className="text-blue-500" />}
                    Shift
                  </span>
                  <span className="font-medium capitalize">
                    {qrGenerated.shift_type} ({qrGenerated.shift_start} - {qrGenerated.shift_end})
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin size={14} /> Location
                  </span>
                  <span className="font-medium">{qrGenerated.location}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <IndianRupee size={14} /> Conveyance
                  </span>
                  <span className="font-medium text-green-600">₹{qrGenerated.conveyance_amount}</span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={copyQRData}
                >
                  {qrCopied ? (
                    <><Check size={16} className="mr-2" /> Copied!</>
                  ) : (
                    <><Copy size={16} className="mr-2" /> Copy Data</>
                  )}
                </Button>
                <Button 
                  className="flex-1 bg-[#1E2A5E] hover:bg-[#2D3A8C]"
                  onClick={downloadQR}
                >
                  <Download size={16} className="mr-2" /> Download
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Share this QR code with your team members for attendance marking
              </p>
              
              <DialogFooter>
                <Button variant="outline" onClick={resetQRDialog}>
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    setQrGenerated(null);
                    setQrForm({ ...qrForm, location: '', conveyance_amount: '' });
                  }}
                  className="bg-[#1E2A5E] hover:bg-[#2D3A8C]"
                >
                  Generate Another
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Team Attendance Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet size={20} className="text-green-600" />
              Export Team Attendance
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Export Type Selection */}
            <div className="space-y-2">
              <Label className="text-sm">Export Type</Label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Month-wise Export</SelectItem>
                  <SelectItem value="dateRange">Date Range Export</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Month-wise Selection */}
            {exportType === 'month' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Month</Label>
                  <Select value={exportMonth.toString()} onValueChange={(v) => setExportMonth(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                        <SelectItem key={i} value={(i+1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Year</Label>
                  <Select value={exportYear.toString()} onValueChange={(v) => setExportYear(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {/* Date Range Selection */}
            {exportType === 'dateRange' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Start Date</Label>
                  <Input 
                    type="date" 
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                    data-testid="export-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">End Date</Label>
                  <Input 
                    type="date" 
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                    data-testid="export-end-date"
                  />
                </div>
              </div>
            )}
            
            {/* Include Manpower Checkbox */}
            <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
              <input 
                type="checkbox" 
                id="includeManpower"
                checked={includeManpower}
                onChange={(e) => setIncludeManpower(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <Label htmlFor="includeManpower" className="text-sm cursor-pointer flex items-center gap-2">
                <HardHat size={14} className="text-purple-600" />
                Include Manpower Entries
              </Label>
            </div>
            
            {/* Info */}
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
              <p className="font-medium mb-1">Export will include:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>All team members&apos; attendance</li>
                {includeManpower && <li>Manpower sourcing entries</li>}
                <li>Conveyance and duty amounts</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport}
              className="bg-green-600 hover:bg-green-700"
              data-testid="export-submit-btn"
            >
              <Download size={16} className="mr-2" /> Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manpower Person Detail Dialog */}
      <Dialog open={showManpowerDetail} onOpenChange={setShowManpowerDetail}>
        <DialogContent className="max-w-lg" data-testid="manpower-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-sm">
                {selectedManpower?.name?.split(' ').map(n => n[0]).join('').slice(0,2) || 'MP'}
              </div>
              <div>
                <span className="text-lg">{selectedManpower?.name}</span>
                <p className="text-sm font-normal text-gray-500">Monthly Work Record</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedManpower && (
            <div className="space-y-4 py-2">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-700">{selectedManpower.daysWorked}</p>
                  <p className="text-xs text-gray-500">Days Worked</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">₹{selectedManpower.totalEarned.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Total Earned</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">₹{selectedManpower.totalConveyance.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Conveyance</p>
                </div>
              </div>
              
              {/* Grand Total */}
              <div className="bg-purple-100 p-3 rounded-lg flex justify-between items-center">
                <span className="font-medium text-purple-800">Grand Total</span>
                <span className="text-xl font-bold text-purple-800">
                  ₹{(selectedManpower.totalEarned + selectedManpower.totalConveyance).toLocaleString()}
                </span>
              </div>
              
              {/* Daily Records Table */}
              <div>
                <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                  <Calendar size={14} /> Daily Records
                </h4>
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium text-gray-600">Date</th>
                        <th className="text-left p-2 font-medium text-gray-600">Location</th>
                        <th className="text-right p-2 font-medium text-gray-600">Earned</th>
                        <th className="text-right p-2 font-medium text-gray-600">Conv</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedManpower.entries
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((entry, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-2">
                            {new Date(entry.date).toLocaleDateString('en-US', { 
                              weekday: 'short', month: 'short', day: 'numeric' 
                            })}
                          </td>
                          <td className="p-2 text-gray-600">{entry.location}</td>
                          <td className="p-2 text-right font-medium text-purple-700">₹{entry.daily_earned || 0}</td>
                          <td className="p-2 text-right text-green-600">₹{entry.conveyance || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManpowerDetail(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Team;
